/**
 * BulkActionBar — Iter-306
 *
 * Sticky bottom bar rendered on /client/shifts when bulk-mode is ON.
 * Exposes selection controls (Select All / Select Visible / Clear) +
 * 4 bulk actions (Archive / Restore / Cancel / Delete) with a
 * confirmation dialog that shows the affected count and action verb.
 *
 * All buttons are gated on the selection state. Cancel opens a small
 * reason textarea because the underlying single-shift cancel endpoint
 * requires a reason.
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Archive, Undo2, XCircle, Trash2, Loader2, CheckSquare, Square, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * @param {object} props
 * @param {string[]} props.selectedIds        — currently selected shift ids
 * @param {string[]} props.visibleIds         — ids currently rendered on screen (filtered list)
 * @param {string[]} props.allIds             — ALL ids the user could select (unfiltered list)
 * @param {function} props.onSelectAll        — toggle: pick all `allIds`
 * @param {function} props.onSelectVisible    — pick all `visibleIds`
 * @param {function} props.onClearSelection   — clear selection
 * @param {function} props.onExitBulkMode     — leave bulk-mode entirely
 * @param {function} props.onCompleted        — reload the list after an action succeeds
 */
const BulkActionBar = ({
  selectedIds, visibleIds, allIds,
  onSelectAll, onSelectVisible, onClearSelection,
  onExitBulkMode, onCompleted,
}) => {
  const [confirm, setConfirm] = useState(null);   // { action, verb, gerund }
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);

  const count = selectedIds.length;
  const hasSelection = count > 0;

  const allSelected = useMemo(
    () => allIds.length > 0 && allIds.every((id) => selectedIds.includes(id)),
    [allIds, selectedIds],
  );

  const _run = async (action, reason) => {
    setBusy(true);
    try {
      const res = await api.post('/client/shifts/bulk-action', {
        action,
        shift_ids: selectedIds,
        cancel_reason: reason || null,
      });
      const { succeeded = [], failed = [] } = res.data || {};
      if (succeeded.length > 0 && failed.length === 0) {
        toast.success(`${succeeded.length} event${succeeded.length === 1 ? '' : 's'} ${verbFor(action, 'past')}`);
      } else if (succeeded.length > 0 && failed.length > 0) {
        toast.warning(`${succeeded.length} ${verbFor(action, 'past')}, ${failed.length} skipped`);
        // Show first 3 reasons.
        failed.slice(0, 3).forEach((f) => toast.info(`Skipped: ${f.detail}`, { duration: 5000 }));
      } else {
        // Nothing succeeded — surface each failure.
        toast.error(`No events ${verbFor(action, 'past')}. ${failed[0]?.detail || 'Unknown error'}`);
      }
      setConfirm(null);
      setCancelReason('');
      onCompleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${verbFor(action, 'infinitive')}`);
    } finally {
      setBusy(false);
    }
  };

  const startAction = (action) => {
    setConfirm({
      action,
      verb: verbFor(action, 'infinitive'),
      gerund: verbFor(action, 'gerund'),
    });
  };

  return (
    <>
      {/* Sticky bar — bottom on mobile, bottom-center on desktop. */}
      <div
        className="fixed left-0 right-0 bottom-0 z-40 border-t border-cyan-200 bg-white/95 backdrop-blur shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.15)]"
        data-testid="bulk-action-bar"
      >
        <div className="mx-auto max-w-7xl px-3 sm:px-6 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700 shrink-0">
            <CheckSquare className="w-4 h-4 text-cyan-600" />
            <span data-testid="bulk-selection-count">
              {count} selected
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <Button
              size="sm" variant="ghost"
              className="h-8 text-xs text-slate-700 hover:bg-cyan-50"
              onClick={onSelectAll}
              data-testid="bulk-select-all"
              disabled={allIds.length === 0}
            >
              {allSelected ? <CheckSquare className="w-3.5 h-3.5 mr-1" /> : <Square className="w-3.5 h-3.5 mr-1" />}
              Select all ({allIds.length})
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-8 text-xs text-slate-700 hover:bg-cyan-50"
              onClick={onSelectVisible}
              data-testid="bulk-select-visible"
              disabled={visibleIds.length === 0}
            >
              Select visible ({visibleIds.length})
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-8 text-xs text-slate-500 hover:bg-slate-50"
              onClick={onClearSelection}
              disabled={!hasSelection}
              data-testid="bulk-clear-selection"
            >
              Clear
            </Button>
          </div>

          <div className="flex-1" />

          {/* Action buttons — labels hide on very small screens to fit in 2 rows instead of 4. */}
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm" variant="outline"
              className="h-8 text-xs border-slate-200 text-slate-700 hover:bg-slate-100"
              disabled={!hasSelection || busy}
              onClick={() => startAction('archive')}
              data-testid="bulk-archive"
            >
              <Archive className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Archive</span>
            </Button>
            <Button
              size="sm" variant="outline"
              className="h-8 text-xs border-slate-200 text-slate-700 hover:bg-slate-100"
              disabled={!hasSelection || busy}
              onClick={() => startAction('unarchive')}
              data-testid="bulk-unarchive"
            >
              <Undo2 className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Restore</span>
            </Button>
            <Button
              size="sm" variant="outline"
              className="h-8 text-xs border-amber-200 text-amber-800 hover:bg-amber-50"
              disabled={!hasSelection || busy}
              onClick={() => startAction('cancel')}
              data-testid="bulk-cancel"
            >
              <XCircle className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Cancel</span>
            </Button>
            <Button
              size="sm" variant="outline"
              className="h-8 text-xs border-rose-200 text-rose-800 hover:bg-rose-50"
              disabled={!hasSelection || busy}
              onClick={() => startAction('delete')}
              data-testid="bulk-delete"
            >
              <Trash2 className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Delete</span>
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-8 text-xs text-slate-500 hover:bg-slate-50 ml-1"
              onClick={onExitBulkMode}
              data-testid="bulk-exit"
              title="Exit bulk mode"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={!!confirm} onOpenChange={(v) => { if (!v && !busy) { setConfirm(null); setCancelReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize" data-testid="bulk-confirm-title">
              {confirm?.verb} {count} event{count === 1 ? '' : 's'}?
            </DialogTitle>
            <DialogDescription>
              You are about to {confirm?.verb} <strong>{count}</strong> event{count === 1 ? '' : 's'}.
              {confirm?.action === 'delete' && ' This cannot be undone. Deletion is only allowed for open or cancelled events with no active bookings and no invoices — other events will be skipped.'}
              {confirm?.action === 'cancel' && ' Booked talent will be notified of the cancellation.'}
              {confirm?.action === 'archive' && ' Archived events are hidden from the default list but not deleted.'}
              {confirm?.action === 'unarchive' && ' Restored events return to your default list.'}
            </DialogDescription>
          </DialogHeader>

          {confirm?.action === 'cancel' && (
            <div>
              <Label className="text-xs text-slate-600">Cancellation reason (shown to talent)</Label>
              <Textarea
                className="mt-1"
                rows={2}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Client postponed the event"
                data-testid="bulk-cancel-reason"
              />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline" size="sm" disabled={busy}
              onClick={() => { setConfirm(null); setCancelReason(''); }}
              data-testid="bulk-confirm-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className={
                confirm?.action === 'delete' ? 'bg-rose-600 hover:bg-rose-700 text-white'
                  : confirm?.action === 'cancel' ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'bg-cyan-600 hover:bg-cyan-700 text-white'
              }
              disabled={busy || (confirm?.action === 'cancel' && !cancelReason.trim())}
              onClick={() => _run(confirm.action, cancelReason)}
              data-testid="bulk-confirm-run"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Yes, {confirm?.verb} {count}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

function verbFor(action, form) {
  const map = {
    archive:   { infinitive: 'archive',   gerund: 'archiving',   past: 'archived'   },
    unarchive: { infinitive: 'restore',   gerund: 'restoring',   past: 'restored'   },
    cancel:    { infinitive: 'cancel',    gerund: 'cancelling',  past: 'cancelled'  },
    delete:    { infinitive: 'delete',    gerund: 'deleting',    past: 'deleted'    },
  };
  return map[action]?.[form] || action;
}

export default BulkActionBar;
